import axios, {
  AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig,
} from "axios";
import { AuthStorageService } from "@/features/auth/services/storage.service";
import { TokenRefreshManager } from "@/features/auth/utils/tokenRefresher";
import { authSessionBus } from "@/shared/lib/authSessionBus";
import { storageService, type StorageService } from "@/shared/services/localStorage.service";
import { getApiBaseUrl } from "@/shared/utils/api.util";

const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || "30000");
const API_BASE_URL = getApiBaseUrl();
const API_REQUEST_HEADERS = {
  "Content-Type": "application/json",
  "X-Client-Type": "web",
};

const HTTP_FORBIDDEN = 403;
const LEGAL_ACCEPTANCE_REQUIRED_ERROR_CODE = "legal_acceptance_required";

const hasLegalAcceptanceRequiredCode = (body: unknown): boolean =>
  typeof body === "object" && body !== null && "error" in body &&
  typeof body.error === "object" && body.error !== null && "code" in body.error &&
  body.error.code === LEGAL_ACCEPTANCE_REQUIRED_ERROR_CODE;

const isLegalAcceptanceRequiredError = (error: AxiosError): boolean =>
  error.response?.status === HTTP_FORBIDDEN && hasLegalAcceptanceRequiredCode(error.response.data);

export type Fetcher = AxiosInstance & { (config: InternalAxiosRequestConfig): Promise<AxiosResponse> };

const serializeParams = (params: Record<string, unknown>, prefix?: string): string[] => {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    const fullKey = prefix ? `${prefix}[${key}]` : key;
    if (Array.isArray(value)) {
      for (const item of value) {
        parts.push(`${encodeURIComponent(`${fullKey}[]`)}=${encodeURIComponent(String(item))}`);
      }
    }
    else if (typeof value === "object") {
      parts.push(...serializeParams(value as Record<string, unknown>, fullKey));
    }
    else {
      parts.push(`${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts;
};

const fetcherConfig = {
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: API_REQUEST_HEADERS,
  withCredentials: true,
  paramsSerializer: (params: Record<string, unknown>) => serializeParams(params).join("&"),
};

export class FetcherService {
  private fetcher: Fetcher = axios.create(fetcherConfig);

  private fetcherWithout401Retry: Fetcher = axios.create(fetcherConfig);

  private tokenRefreshManager: TokenRefreshManager;

  private authStorageService: AuthStorageService;

  constructor(storageService: StorageService) {
    this.authStorageService = new AuthStorageService(storageService);
    this.tokenRefreshManager = new TokenRefreshManager(this.fetcher, this.authStorageService);
    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.setupFetcherInterceptors();
    this.setupFetcherWithout401RetryInterceptors();
  }

  private setTokenInHeaders(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
    const authToken = this.authStorageService.getAuthToken();
    // An explicit header wins, `Authorization: false` included (a request sent without token).
    if (authToken && config.headers.Authorization === undefined) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
  }

  private setupFetcherInterceptors(): void {
    this.fetcher.interceptors.request.use((config) => {
      return this.setTokenInHeaders(config);
    }, (error) => Promise.reject(error));

    this.fetcher.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        const authToken = this.authStorageService.getAuthToken();

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (authToken) {
            return this.tokenRefreshManager.handleTokenRefresh(originalRequest);
          }

          authSessionBus.triggerUnauthorized();
        }

        if (isLegalAcceptanceRequiredError(error)) {
          authSessionBus.triggerLegalAcceptanceRequired();
        }

        return Promise.reject(error);
      },
    );
  }

  private setupFetcherWithout401RetryInterceptors(): void {
    this.fetcherWithout401Retry.interceptors.request.use((config) => {
      return this.setTokenInHeaders(config);
    }, (error) => Promise.reject(error));
  }

  getFetcherInstance(): Fetcher {
    return this.fetcher;
  }

  getFetcherWithout401RetryInstance(): Fetcher {
    return this.fetcherWithout401Retry;
  }
}

// Default instance: the device id lives in the localStorage, the access token in memory
const fetcherService = new FetcherService(storageService);
export const fetcher = fetcherService.getFetcherInstance();
export const fetcherWithout401Retry = fetcherService.getFetcherWithout401RetryInstance();
